import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Badge,
  Button,
  Code,
  Container,
  Group,
  List,
  Loader,
  Modal,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import {
  IconBrandTelegram,
  IconCheck,
  IconCopy,
  IconExternalLink,
  IconLink,
  IconRefresh,
  IconUnlink,
  IconX,
} from '@tabler/icons-react'
import type { TgLinkCodeDto, TgLinkStatusDto } from '@ledokol/contracts'
import { AppLayout } from '../features/projects/app-shell'
import { deriveMeTgState, type MeTgState } from '../shared/me-tg-state'
import {
  cancelTgLink,
  confirmTgLink,
  createTgLinkCode,
  fetchTgLinkStatus,
  unlinkTg,
} from '../shared/api/tg-link'
import { useTgLinkPolling } from '../shared/use-tg-link-polling'
import { refreshMe } from '../features/auth/store'

/**
 * Эпик «UI tg-link сотрудника» (T12) P3 — основная страница.
 *
 * State machine: deriveMeTgState из P1 + useTgLinkPolling из P2 — НЕ дублируем логику.
 * UI **реактивно следует за поллингом** (требование P3 #1): когда хук вернул новый
 * status — useEffect перекладывает его в локальный state, derive прокидывает в UI без
 * перезагрузки страницы. Initial fetch при mount даёт начальный state.
 *
 * State B (две кнопки — §3.B-fix плана, требование P3 #2): «Открыть в Telegram»
 * (tg://resolve) И «Копировать код» — **обе доступны одновременно**, не fallback.
 * Копирование работает всегда; tg://resolve может молча провалиться в браузере без
 * Telegram Desktop. Текст-объяснение про оба пути обязателен (не tooltip).
 *
 * Unlink (state D → A): confirm-модалка с РЕАЛЬНЫМ текстом 3 последствий (§1.3 плана,
 * требование P3 #3) — задачи остаются (✓), tg обнуляется (✗), можно привязать другой (✓).
 */

const BOT_USERNAME = (import.meta.env.VITE_BOT_USERNAME as string | undefined) ?? 'ldklcrm_bot'

export function MeTelegramPage() {
  const [status, setStatus] = useState<TgLinkStatusDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [code, setCode] = useState<TgLinkCodeDto | null>(null) // храним последний выпущенный код локально (api возвращает только при POST)
  const [busy, setBusy] = useState(false) // на время mutations (создать код / confirm / cancel / unlink)
  const [unlinkOpen, setUnlinkOpen] = useState(false)

  const derived: MeTgState = useMemo(() => (status ? deriveMeTgState(status) : 'pristine'), [status])

  // Поллинг включён ТОЛЬКО в state B — экономия api-запросов (план §1.2 #G)
  const polling = useTgLinkPolling(derived === 'code-pending-redeem')

  // Когда поллинг возвращает новый status — реактивно обновляем local state
  // (это и есть требование P3 #1: UI следует за onStatus, не залипает на mount fetchMe)
  useEffect(() => {
    if (polling.status) setStatus(polling.status)
  }, [polling.status])

  // Initial fetch при mount
  useEffect(() => {
    void reloadStatus().finally(() => setLoading(false))
  }, [])

  // Если перешли в D (linked) после получения кода — код больше не релевантен
  useEffect(() => {
    if (derived === 'linked' || derived === 'pristine') setCode(null)
  }, [derived])

  async function reloadStatus(): Promise<void> {
    try {
      const s = await fetchTgLinkStatus()
      setStatus(s)
    } catch (e) {
      notifications.show({ color: 'red', title: 'Не удалось загрузить', message: String(e) })
    }
  }

  // ── Actions ───────────────────────────────────────────────────────────────

  const onGetCode = useCallback(async () => {
    setBusy(true)
    try {
      const c = await createTgLinkCode()
      setCode(c)
      await reloadStatus() // обновим status — теперь has_active_code=true → state B → поллинг включится
    } catch (e) {
      notifications.show({ color: 'red', message: String(e) })
    } finally {
      setBusy(false)
    }
  }, [])

  const onCancel = useCallback(async () => {
    setBusy(true)
    try {
      await cancelTgLink()
      setCode(null)
      await reloadStatus()
    } catch (e) {
      notifications.show({ color: 'red', message: String(e) })
    } finally {
      setBusy(false)
    }
  }, [])

  const onConfirm = useCallback(async () => {
    setBusy(true)
    try {
      await confirmTgLink()
      notifications.show({ color: 'green', title: 'Привязано', message: 'Telegram теперь связан с вашим аккаунтом.' })
      // Локальный state страницы обновим, и глобальный user в Zustand-сторе —
      // иначе shell-баннер «привязать Telegram» (читает user.tg_linked_at)
      // продолжит висеть на других страницах до F5.
      await Promise.all([reloadStatus(), refreshMe()])
    } catch (e) {
      notifications.show({ color: 'red', title: 'Не удалось подтвердить', message: String(e) })
    } finally {
      setBusy(false)
    }
  }, [])

  const onUnlinkConfirmed = useCallback(async () => {
    setBusy(true)
    try {
      await unlinkTg()
      notifications.show({ color: 'gray', message: 'Telegram отвязан.' })
      setCode(null)
      // Симметрично с confirm: после unlink баннер должен СНОВА появиться;
      // без refreshMe глобальный user остался бы с tg_linked_at != null.
      await Promise.all([reloadStatus(), refreshMe()])
      setUnlinkOpen(false)
    } catch (e) {
      notifications.show({ color: 'red', message: String(e) })
    } finally {
      setBusy(false)
    }
  }, [])

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading && !status) {
    return (
      <AppLayout>
        <Container size="sm" py="md">
          <Loader />
        </Container>
      </AppLayout>
    )
  }

  return (
    <AppLayout>
      <Container size="sm" py="md">
        <Group justify="space-between" mb="md">
          <Group gap="xs">
            <IconBrandTelegram size={28} stroke={1.6} color="var(--mantine-color-blue-6)" />
            <Title order={2}>Мой Telegram</Title>
          </Group>
          {derived === 'linked' && (
            <Badge color="green" variant="light" size="lg">
              Привязано
            </Badge>
          )}
        </Group>

        {derived === 'pristine' && (
          <PristineSection onGetCode={onGetCode} busy={busy} />
        )}

        {derived === 'code-pending-redeem' && code && (
          <CodePendingSection code={code} onCancel={onCancel} busy={busy} pollingStopReason={polling.stopReason} />
        )}

        {/* Если перезагрузили страницу в состоянии B (код есть на сервере, но мы не помним) —
            подсказываем сделать заново. Это редкий кейс (свежий код был выпущен и забыт). */}
        {derived === 'code-pending-redeem' && !code && (
          <Alert color="yellow" variant="light" mb="md">
            На сервере есть активный код привязки, но он не показан здесь (страница была перезагружена).
            Получите новый код (старый отменится автоматически при истечении TTL).
            <Group mt="sm">
              <Button onClick={onGetCode} loading={busy}>
                Получить новый код
              </Button>
              <Button variant="default" onClick={onCancel} loading={busy}>
                Отменить активный
              </Button>
            </Group>
          </Alert>
        )}

        {derived === 'awaiting-confirm' && status?.pending && (
          <AwaitingConfirmSection
            pendingUsername={status.pending.tg_username}
            onConfirm={onConfirm}
            onCancel={onCancel}
            busy={busy}
          />
        )}

        {derived === 'linked' && status && (
          <LinkedSection
            tgUsername={status.tg_username}
            onUnlinkClick={() => setUnlinkOpen(true)}
            busy={busy}
          />
        )}

        <UnlinkConfirmModal
          opened={unlinkOpen}
          tgUsername={status?.tg_username ?? null}
          busy={busy}
          onClose={() => setUnlinkOpen(false)}
          onConfirm={onUnlinkConfirmed}
        />
      </Container>
    </AppLayout>
  )
}

// ── State A: pristine ────────────────────────────────────────────────────────

function PristineSection({ onGetCode, busy }: { onGetCode: () => void; busy: boolean }) {
  return (
    <Paper p="md" withBorder>
      <Stack>
        <Text>
          Telegram-аккаунт ещё не привязан к CRM. Привязка нужна, чтобы вы могли{' '}
          <b>подтверждать карточки задач</b> кнопкой в групповых чатах и чтобы задачи из чатов
          попадали в CRM с вами как «постановщиком», а не на временную учётку.
        </Text>
        <Text size="sm" c="dimmed">
          Это безопасный двух-шаговый процесс: вы получите 6-значный код, отправите его боту в личке,
          вернётесь сюда и подтвердите свой @username.
        </Text>
        <Group>
          <Button leftSection={<IconLink size={16} />} onClick={onGetCode} loading={busy}>
            Получить код
          </Button>
        </Group>
      </Stack>
    </Paper>
  )
}

// ── State B: code-pending-redeem ─────────────────────────────────────────────

function CodePendingSection({
  code,
  onCancel,
  busy,
  pollingStopReason,
}: {
  code: TgLinkCodeDto
  onCancel: () => void
  busy: boolean
  pollingStopReason: ReturnType<typeof useTgLinkPolling>['stopReason']
}) {
  // tg://resolve?domain=ldklcrm_bot&text=/start%20XXXXXX
  // text — encodeURIComponent целиком («/start XXXXXX» с пробелом → /start%20XXXXXX)
  const tgDeepLink = `tg://resolve?domain=${BOT_USERNAME}&text=${encodeURIComponent('/start ' + code.code)}`

  function onCopy() {
    void navigator.clipboard.writeText(code.code).then(
      () => notifications.show({ color: 'green', message: 'Код скопирован' }),
      () => notifications.show({ color: 'red', message: 'Не удалось скопировать (clipboard API заблокирован?)' }),
    )
  }

  const expiresMin = Math.max(0, Math.round((new Date(code.expires_at).getTime() - Date.now()) / 60_000))
  // Если поллинг сам остановился по TTL — код истёк
  const ttlExpired = pollingStopReason === 'ttl'

  return (
    <Paper p="md" withBorder>
      <Stack>
        <Text>
          Ваш код привязки:
        </Text>
        <Group justify="center">
          <Code style={{ fontSize: '2rem', padding: '0.5rem 1rem', letterSpacing: '0.25em' }}>
            {code.code}
          </Code>
        </Group>
        <Text size="sm" c="dimmed" ta="center">
          Срок действия — {expiresMin} мин. {ttlExpired && '⚠️ Код истёк, запросите новый.'}
        </Text>

        {/* §3.B-fix: ОБЕ кнопки доступны одновременно. tg://resolve работает на мобильном
            и desktop'е с установленным Telegram; clipboard — везде. */}
        <Group grow>
          <Button
            component="a"
            href={tgDeepLink}
            leftSection={<IconExternalLink size={16} />}
            disabled={ttlExpired}
          >
            Открыть в Telegram
          </Button>
          <Button
            variant="default"
            leftSection={<IconCopy size={16} />}
            onClick={onCopy}
            disabled={ttlExpired}
          >
            Копировать код
          </Button>
        </Group>

        <Alert variant="light" color="blue">
          <Stack gap={4}>
            <Text size="sm">
              <b>На мобильном или с Telegram Desktop:</b> нажмите «Открыть в Telegram» — откроется
              бот <Code>@{BOT_USERNAME}</Code> с подставленной командой <Code>/start {code.code}</Code>, нужно лишь нажать «Отправить».
            </Text>
            <Text size="sm">
              <b>Если Telegram не открылся</b> (приложение не установлено, ссылка заблокирована
              браузером) — нажмите «Копировать код», откройте бота{' '}
              <Text component="span" fw={500}>@{BOT_USERNAME}</Text> в Telegram вручную и отправьте:{' '}
              <Code>/start {code.code}</Code>.
            </Text>
          </Stack>
        </Alert>

        <Text size="xs" c="dimmed" ta="center">
          После отправки кода боту вернитесь на эту страницу — следующий шаг (подтверждение @username)
          появится автоматически в течение нескольких секунд.
        </Text>

        <Group justify="space-between">
          <Button variant="subtle" color="gray" leftSection={<IconX size={14} />} onClick={onCancel} loading={busy}>
            Отменить код
          </Button>
        </Group>
      </Stack>
    </Paper>
  )
}

// ── State C: awaiting-confirm ────────────────────────────────────────────────

function AwaitingConfirmSection({
  pendingUsername,
  onConfirm,
  onCancel,
  busy,
}: {
  pendingUsername: string | null
  onConfirm: () => void
  onCancel: () => void
  busy: boolean
}) {
  return (
    <Paper p="md" withBorder>
      <Stack>
        <Alert color="green" variant="light" icon={<IconCheck size={20} />}>
          Бот получил ваш код. Проверьте, ваш ли это аккаунт ниже.
        </Alert>
        <Text>Бот сообщает: код пришёл от</Text>
        <Group justify="center">
          <Badge size="xl" variant="filled" color="blue">
            @{pendingUsername ?? '(без username)'}
          </Badge>
        </Group>
        <Text size="sm" c="dimmed">
          Если это <b>ваш</b> Telegram — подтвердите. Если @username чужой (например, код успел перехватить
          кто-то другой) — нажмите «Это не я», ваш аккаунт CRM не пострадает и можно будет начать
          привязку заново.
        </Text>
        <Group grow>
          <Button leftSection={<IconCheck size={16} />} onClick={onConfirm} loading={busy} color="green">
            Это я — подтвердить
          </Button>
          <Button leftSection={<IconX size={16} />} onClick={onCancel} loading={busy} variant="default">
            Это не я
          </Button>
        </Group>
      </Stack>
    </Paper>
  )
}

// ── State D: linked ──────────────────────────────────────────────────────────

function LinkedSection({
  tgUsername,
  onUnlinkClick,
  busy,
}: {
  tgUsername: string | null
  onUnlinkClick: () => void
  busy: boolean
}) {
  return (
    <Paper p="md" withBorder>
      <Stack>
        <Alert color="green" variant="light" icon={<IconCheck size={20} />}>
          <Text>
            Telegram привязан: <Code>@{tgUsername ?? '(без username)'}</Code>
          </Text>
        </Alert>
        <Text size="sm" c="dimmed">
          В привязанных рабочих чатах вы будете виден как «постановщик» в задачах из ваших сообщений
          и сможете подтверждать карточки кнопкой. Если что-то с TG-аккаунтом не так — отвяжите и
          привяжите заново.
        </Text>
        <Group>
          <Button
            variant="default"
            color="red"
            leftSection={<IconUnlink size={16} />}
            onClick={onUnlinkClick}
            disabled={busy}
          >
            Отвязать
          </Button>
          {/* Refresh — для случая если status изменился во внешнем процессе (admin unlink, скажем) */}
          <Button
            variant="subtle"
            color="gray"
            leftSection={<IconRefresh size={16} />}
            onClick={() => window.location.reload()}
          >
            Обновить
          </Button>
        </Group>
      </Stack>
    </Paper>
  )
}

// ── Confirm-модалка отвязки (требование P3 #3 — реальный текст 3 последствий) ─

function UnlinkConfirmModal({
  opened,
  tgUsername,
  busy,
  onClose,
  onConfirm,
}: {
  opened: boolean
  tgUsername: string | null
  busy: boolean
  onClose: () => void
  onConfirm: () => void
}) {
  return (
    <Modal opened={opened} onClose={onClose} title="Отвязать Telegram?" centered>
      <Stack>
        <Text>
          Telegram <Code>@{tgUsername ?? '(без username)'}</Code> будет отвязан от вашего аккаунта.
        </Text>
        <Alert color="yellow" variant="light">
          <Text size="sm" mb="xs" fw={500}>
            Что произойдёт:
          </Text>
          <List
            spacing="xs"
            size="sm"
            icon={null}
            styles={{
              itemWrapper: { display: 'inline' },
            }}
          >
            <List.Item icon={<IconCheck size={16} color="var(--mantine-color-green-7)" />}>
              <b>Задачи останутся.</b> Все задачи, где вы постановщик или исполнитель, никуда не
              денутся — они привязаны к вашему профилю в CRM, не к TG-аккаунту.
            </List.Item>
            <List.Item icon={<IconX size={16} color="var(--mantine-color-orange-7)" />}>
              <b>Бот перестанет узнавать вас в чатах.</b> Ваши сообщения станут «непривязанными» —
              создадут временную учётку, и кнопкой [Подтвердить] на карточке вы пользоваться не
              сможете (вернёт «нет прав»).
            </List.Item>
            <List.Item icon={<IconCheck size={16} color="var(--mantine-color-green-7)" />}>
              <b>Можно привязать ДРУГОЙ Telegram-аккаунт</b> (например, если сменили телефон) —
              пройдёте этот же процесс заново.
            </List.Item>
          </List>
        </Alert>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={busy}>
            Отмена
          </Button>
          <Button color="red" onClick={onConfirm} loading={busy} leftSection={<IconUnlink size={16} />}>
            Отвязать
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
